import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { catchError, finalize, Observable, switchMap, tap, throwError } from 'rxjs';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { ErrorHandlerService } from './error-handler.service';
import { AuthService } from '../../shared/services/auth.service';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { CorrelationIdService } from './telemetry';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class InterceptorService implements HttpInterceptor {
  private refreshTokenRequest?: Observable<any>;
  private correlationIdService: CorrelationIdService | null = null;

  constructor(
    private readonly errorHandler: ErrorHandlerService,
    private readonly spinnerService: SpinnerService,
    private readonly authService: AuthService,
    private readonly injector: Injector
  ) {}

  /**
   * Lazy initialization of correlation ID service to avoid circular dependencies
   */
  private initCorrelationIdService(): void {
    this.correlationIdService ??= this.injector.get(CorrelationIdService);
  }

  /**
   * Check if the request is to the telemetry endpoint (to avoid adding headers)
   */
  private isTelemetryRequest(url: string): boolean {
    return url.includes('/v1/telemetry/');
  }

  /**
   * Check if the request is to our internal API (not external services)
   */
  private isInternalApiRequest(url: string): boolean {
    const apiBaseUrl = environment.API_BASE_URL;

    // Check if URL starts with our API base URL or is a relative URL
    return (
      url.startsWith(apiBaseUrl) ||
      url.startsWith('/') || // Relative URLs are internal
      !url.startsWith('http') // Non-http URLs are internal
    );
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.initCorrelationIdService();

    // Only add correlation headers to internal API requests (not external services)
    const shouldAddHeaders =
      this.isInternalApiRequest(request.url) &&
      !this.isTelemetryRequest(request.url);

    // Clone request with correlation ID header for internal APIs only
    let modifiedRequest = request;
    if (shouldAddHeaders && this.correlationIdService) {
      const correlationId = this.correlationIdService.getCorrelationId();
      const messageId = this.correlationIdService.generateMessageId();

      modifiedRequest = request.clone({
        setHeaders: {
          'X-Correlation-ID': correlationId,
          'X-Message-ID': messageId,
        },
      });
    }

    return next.handle(modifiedRequest).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(modifiedRequest, next);
        }

        return throwError(() => error);
      }),
      tap({
        next: event => {
          if (event instanceof HttpResponse) {
            this.spinnerService.hideSpinner();
          }
        },
        error: _error => {
          if (_error instanceof HttpErrorResponse) {
            this.spinnerService.hideSpinner();
            // Generic toast only for server/connection failures, or client
            // errors with no business `code` (coded errors are shown by the page).
            const showToast =
              _error.status >= 500 ||
              _error.status === 0 ||
              (_error.status !== 401 &&
                !_error.error?.code &&
                !this.isTelemetryRequest(modifiedRequest.url));
            if (showToast) {
              this.errorHandler.handleError(_error);
            }
          }
        },
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    this.refreshTokenRequest ??= this.authService.refreshSession().pipe(
      finalize(() => {
        this.refreshTokenRequest = undefined;
      })
    );

    return this.refreshTokenRequest.pipe(
      switchMap(() => {
        return next.handle(
          request.clone({
            headers: request.headers.set('authorization', this.authService.getJWTToken()!),
          })
        );
      }),
      catchError(error => {
        if (
          error.status === 403 ||
          error.status === 401 ||
          error instanceof InteractionRequiredAuthError
        ) {
          // Initiate logout
          // show popup first and then initiate logout.
          this.spinnerService.hideSpinner();
          this.authService.showWarningPopupAndLogout();
        }

        return throwError(() => error);
      })
    );
  }
}
